import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useToast } from '../hooks/useToast';
import { isValidEmail } from '../utils/validation';

type Step = 'email' | 'code' | 'newPassword' | 'success';

const ForgotPassword = () => {
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const isOnline = useNetworkStatus();
    const { showToast } = useToast();

    const clearMessages = () => {
        setError('');
        setSuccessMessage('');
    };

    const handleEmailSubmit = (e: FormEvent) => {
        e.preventDefault();
        clearMessages();

        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        if (!isValidEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setIsLoading(true);

        // DEV ONLY: Simulated API call
        setTimeout(() => {
            setIsLoading(false);
            // Simulate checking if email exists
            const nonExistingEmails = ['nonexistent@example.com'];

            if (nonExistingEmails.includes(email.toLowerCase())) {
                setError('No account found with this email address. Please check and try again.');
                return;
            }

            setSuccessMessage(`Verification code sent to ${email}. Please check your inbox.`);
            showToast('Verification code sent to your email!', 'success');
            setTimeout(() => {
                setSuccessMessage('');
                setStep('code');
            }, 1500);
        }, 1000);
    };

    const handleCodeSubmit = (e: FormEvent) => {
        e.preventDefault();
        clearMessages();

        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        setIsLoading(true);

        // DEV ONLY: Simulated API call
        setTimeout(() => {
            setIsLoading(false);
            if (code.length === 6 && /^\d+$/.test(code)) {
                setSuccessMessage('Code verified successfully!');
                showToast('Code verified! Create your new password.', 'success');
                setTimeout(() => {
                    setSuccessMessage('');
                    setStep('newPassword');
                }, 1000);
            } else if (code.length !== 6) {
                setError('Verification code must be 6 digits.');
            } else {
                setError('Invalid verification code. Please check and try again.');
            }
        }, 1000);
    };

    const handleResendCode = () => {
        clearMessages();

        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        setIsLoading(true);

        setTimeout(() => {
            setIsLoading(false);
            showToast('New verification code sent to your email!', 'success');
            setSuccessMessage('A new code has been sent to your email.');
        }, 1000);
    };

    const handlePasswordSubmit = (e: FormEvent) => {
        e.preventDefault();
        clearMessages();

        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match. Please try again.');
            return;
        }

        setIsLoading(true);

        // DEV ONLY: Simulated API call
        setTimeout(() => {
            setIsLoading(false);
            showToast('Password reset successful!', 'success');
            setStep('success');
        }, 1000);
    };

    const renderStepIndicator = () => {
        const steps = ['Email', 'Verify', 'New Password', 'Complete'];
        const currentIndex = ['email', 'code', 'newPassword', 'success'].indexOf(step);

        return (
            <div className="flex items-center justify-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
                {steps.map((stepName, index) => (
                    <div key={stepName} className="flex items-center">
                        <div
                            className="rounded-full flex items-center justify-center font-medium"
                            style={{
                                width: '2rem',
                                height: '2rem',
                                fontSize: 'var(--font-size-sm)',
                                backgroundColor: index <= currentIndex ? 'var(--color-primary-600)' : 'var(--color-gray-200)',
                                color: index <= currentIndex ? 'white' : 'var(--color-gray-600)'
                            }}
                        >
                            {index + 1}
                        </div>
                        {index < steps.length - 1 && (
                            <div
                                style={{
                                    width: '3rem',
                                    height: '0.25rem',
                                    margin: '0 0.25rem',
                                    backgroundColor: index < currentIndex ? 'var(--color-primary-600)' : 'var(--color-gray-200)'
                                }}
                            />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderEmailStep = () => (
        <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
            <div>
                <h3 className="font-medium" style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-sm)' }}>
                    Enter your email
                </h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                    We'll send you a verification code to reset your password.
                </p>
                <label htmlFor="email" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                    Email address
                </label>
                <input
                    id="email"
                    type="email"
                    required
                    className="appearance-none block w-full"
                    style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center font-medium text-white disabled:opacity-50"
                style={{ padding: 'var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-primary-600)' }}
            >
                {isLoading ? 'Sending...' : 'Send verification code'}
            </button>
        </form>
    );

    const renderCodeStep = () => (
        <form onSubmit={handleCodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
            <div>
                <h3 className="font-medium" style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-sm)' }}>
                    Enter verification code
                </h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                    We sent a 6-digit code to <span className="font-medium">{email}</span>
                </p>
                <input
                    type="text"
                    required
                    maxLength={6}
                    className="appearance-none block w-full font-semibold"
                    style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', textAlign: 'center', fontSize: 'var(--font-size-2xl)', letterSpacing: '0.1em' }}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => {
                        clearMessages();
                        setCode(e.target.value.replace(/\D/g, ''));
                    }}
                />
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center font-medium text-white disabled:opacity-50"
                style={{ padding: 'var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-primary-600)' }}
            >
                {isLoading ? 'Verifying...' : 'Verify code'}
            </button>
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setStep('email')}
                    style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                    ← Go back
                </button>
                <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isLoading}
                    className="disabled:opacity-50"
                    style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-primary-600)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                    Resend code
                </button>
            </div>
        </form>
    );

    const renderPasswordStep = () => (
        <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
            <div>
                <h3 className="font-medium" style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-sm)' }}>
                    Create new password
                </h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                    Enter your new password below.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                    <div>
                        <label htmlFor="newPassword" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                            New password
                        </label>
                        <input
                            id="newPassword"
                            type="password"
                            required
                            className="appearance-none block w-full"
                            style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="confirmNewPassword" className="block font-medium" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                            Confirm password
                        </label>
                        <input
                            id="confirmNewPassword"
                            type="password"
                            required
                            className="appearance-none block w-full"
                            style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-surface)', fontSize: 'var(--font-size-sm)' }}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center font-medium text-white disabled:opacity-50"
                style={{ padding: 'var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-primary-600)' }}
            >
                {isLoading ? 'Resetting...' : 'Reset password'}
            </button>
        </form>
    );

    const renderSuccessStep = () => (
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
            <div className="mx-auto flex items-center justify-center" style={{ height: '64px', width: '64px', borderRadius: '50%', backgroundColor: 'var(--color-success-100)' }}>
                <svg
                    style={{ height: '32px', width: '32px', color: 'var(--color-success-600)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            </div>
            <h3 className="font-medium" style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>
                Password reset successful!
            </h3>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                Your password has been reset successfully. You can now sign in with your
                new password.
            </p>
            <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center font-medium text-white"
                style={{ padding: 'var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-primary-600)' }}
            >
                Go to Sign in
            </button>
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)', padding: 'var(--spacing-2xl) var(--spacing-md)' }}>
            <div className="w-full" style={{ maxWidth: '448px', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
                <div>
                    <h2 className="text-center font-extrabold" style={{ marginTop: 'var(--spacing-lg)', fontSize: 'var(--font-size-3xl)', color: 'var(--color-text-primary)' }}>
                        Reset your password
                    </h2>
                </div>

                {renderStepIndicator()}

                {error && (
                    <div className="flex items-center" style={{ backgroundColor: 'var(--color-danger-100)', border: '1px solid var(--color-danger-400)', color: 'var(--color-danger-700)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', gap: 'var(--spacing-sm)' }}>
                        <svg className="flex-shrink-0" style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{error}</span>
                    </div>
                )}

                {successMessage && (
                    <div className="flex items-center" style={{ backgroundColor: 'var(--color-success-100)', border: '1px solid var(--color-success-400)', color: 'var(--color-success-700)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', gap: 'var(--spacing-sm)' }}>
                        <svg className="flex-shrink-0" style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{successMessage}</span>
                    </div>
                )}

                {step === 'email' && renderEmailStep()}
                {step === 'code' && renderCodeStep()}
                {step === 'newPassword' && renderPasswordStep()}
                {step === 'success' && renderSuccessStep()}

                {step !== 'success' && (
                    <div className="text-center">
                        <Link
                            to="/login"
                            className="font-medium"
                            style={{ color: 'var(--color-primary-600)' }}
                        >
                            Back to Sign in
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForgotPassword;
